import React, { useState } from 'react'
import type { Block } from '../../editor/types'
import { Copy, Check } from 'lucide-react'

export type CodeFrame = 'default' | 'terminal' | 'browser'

interface CodeBlockProps {
  block: Block
}

const KEYWORD_PATTERNS: Record<string, RegExp> = {
  javascript: /\b(const|let|var|function|return|if|else|for|while|class|import|export|from|default|async|await|new|this|try|catch|throw|typeof|instanceof)\b/g,
  python: /\b(def|class|return|if|elif|else|for|while|import|from|as|with|try|except|raise|in|not|and|or|is|True|False|None|self|lambda|yield)\b/g,
  ruby: /\b(def|end|class|module|if|elsif|else|unless|do|while|for|return|require|include|attr_accessor|attr_reader|puts|nil|true|false|self|yield|block_given\?)\b/g,
  html: /(<\/?[a-zA-Z][a-zA-Z0-9]*|>|\/>)/g,
  css: /\b(display|margin|padding|color|background|border|font|width|height|position|flex|grid|none|auto|inherit|solid|center)\b/g,
  bash: /\b(if|then|else|elif|fi|for|while|do|done|case|esac|function|return|exit|echo|export|source|alias|cd|ls|grep|awk|sed|cat|rm|cp|mv|mkdir|chmod|sudo|apt|npm|yarn|pip|git|docker|curl|wget)\b/g,
}

function highlightCode(code: string, language: string): React.ReactNode[] {
  const lines = code.split('\n')
  return lines.map((line, lineIdx) => {
    const parts: React.ReactNode[] = []
    let key = 0

    const commentRegex = language === 'python' || language === 'ruby' || language === 'bash' ? /#.*$/ : language === 'html' ? /<!--[\s\S]*?-->/ : /\/\/.*$|\/\*[\s\S]*?\*\//

    const tokens: { text: string; type: 'plain' | 'string' | 'comment' }[] = []

    const commentMatch = line.match(commentRegex)
    let commentStart = commentMatch ? line.indexOf(commentMatch[0]) : -1
    if (commentStart === -1) commentStart = line.length

    const beforeComment = line.slice(0, commentStart)
    const commentPart = line.slice(commentStart)

    let cursor = 0
    const strRegex = /(["'`])(?:(?!\1|\\).|\\.)*\1/g
    let strMatch: RegExpExecArray | null
    while ((strMatch = strRegex.exec(beforeComment)) !== null) {
      if (strMatch.index > cursor) {
        tokens.push({ text: beforeComment.slice(cursor, strMatch.index), type: 'plain' })
      }
      tokens.push({ text: strMatch[0], type: 'string' })
      cursor = strMatch.index + strMatch[0].length
    }
    if (cursor < beforeComment.length) {
      tokens.push({ text: beforeComment.slice(cursor), type: 'plain' })
    }
    if (commentPart) {
      tokens.push({ text: commentPart, type: 'comment' })
    }

    const kwRegex = KEYWORD_PATTERNS[language]
    for (const token of tokens) {
      if (token.type === 'plain' && kwRegex) {
        const kwRegexCopy = new RegExp(kwRegex.source, 'g')
        let lastIdx = 0
        let kwMatch: RegExpExecArray | null
        while ((kwMatch = kwRegexCopy.exec(token.text)) !== null) {
          if (kwMatch.index > lastIdx) {
            parts.push(<span key={key++}>{token.text.slice(lastIdx, kwMatch.index)}</span>)
          }
          parts.push(<span key={key++} className="text-purple-400 font-medium">{kwMatch[0]}</span>)
          lastIdx = kwMatch.index + kwMatch[0].length
        }
        if (lastIdx < token.text.length) {
          parts.push(<span key={key++}>{token.text.slice(lastIdx)}</span>)
        }
      } else if (token.type === 'string') {
        parts.push(<span key={key++} className="text-green-400">{token.text}</span>)
      } else if (token.type === 'comment') {
        parts.push(<span key={key++} className="text-slate-500 italic">{token.text}</span>)
      } else {
        parts.push(<span key={key++}>{token.text}</span>)
      }
    }

    return (
      <div key={lineIdx} className="flex">
        <span className="select-none text-slate-600 w-8 text-right pr-3 shrink-0">{lineIdx + 1}</span>
        <span className="flex-1">{parts.length > 0 ? parts : ' '}</span>
      </div>
    )
  })
}

function getConfig(block: Block) {
  const config = (block.config || {}) as Record<string, unknown>
  return {
    code: (config.code as string) || '',
    language: (config.language as string) || 'javascript',
    frame: (config.frame as CodeFrame) || 'default',
    filename: (config.filename as string) || '',
  }
}

export default function CodeBlock({ block }: CodeBlockProps) {
  const { code, language, frame, filename } = getConfig(block)
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const codeContent = (
    <div className="p-4 overflow-x-auto leading-relaxed" style={{ whiteSpace: 'pre' }}>
      {highlightCode(code, language)}
    </div>
  )

  const copyButton = (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-200 transition-colors"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )

  if (frame === 'terminal') {
    return (
      <div className="rounded-xl overflow-hidden shadow-2xl">
        {/* macOS terminal chrome */}
        <div className="bg-[#1e1e1e] px-4 py-2.5 flex items-center border-b border-[#333]">
          <div className="flex gap-2">
            <div className="size-3 rounded-full bg-[#ff5f57]" />
            <div className="size-3 rounded-full bg-[#febc2e]" />
            <div className="size-3 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex-1 text-center">
            <span className="text-[11px] text-slate-400 font-mono">
              {filename || `${language} — Terminal`}
            </span>
          </div>
          {copyButton}
        </div>
        <div className="bg-[#1a1a2e] text-slate-200 text-sm font-mono">
          {/* Terminal prompt style for first line */}
          <div className="px-4 pt-3 pb-0">
            <span className="text-green-400">$</span>
            <span className="text-slate-500 text-xs ml-2">~/{filename || 'code'}</span>
          </div>
          {codeContent}
        </div>
      </div>
    )
  }

  if (frame === 'browser') {
    return (
      <div className="rounded-xl overflow-hidden shadow-2xl border border-slate-200">
        {/* Browser chrome */}
        <div className="bg-slate-100 px-4 py-2.5 flex items-center gap-3 border-b border-slate-200">
          <div className="flex gap-1.5">
            <div className="size-3 rounded-full bg-red-400" />
            <div className="size-3 rounded-full bg-amber-400" />
            <div className="size-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 bg-white rounded-md px-3 py-1 text-[11px] text-slate-400 font-mono truncate border border-slate-200">
            {filename || `index.${language === 'html' ? 'html' : language === 'css' ? 'css' : 'js'}`}
          </div>
          {copyButton}
        </div>
        <div className="bg-slate-900 text-slate-200 text-sm font-mono">
          {codeContent}
        </div>
      </div>
    )
  }

  // Default frame
  return (
    <div className="relative rounded-xl overflow-hidden bg-slate-900 text-slate-200 text-sm font-mono">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-800/50 border-b border-slate-700">
        <span className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">
          {filename || language}
        </span>
        {copyButton}
      </div>
      {codeContent}
    </div>
  )
}
